import { TranslateService } from '@ngx-translate/core';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator, JsonDisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { CompositeModel } from '../complex/composite.model';
import { EquipmentModel } from '../complex/equipment.model';
import { BooleanModel } from '../primitive/boolean.model';
import { JsonNumberModel, NumberModel } from '../primitive/number.model';
import { JsonTextModel, TextModel } from '../primitive/text.model';

export class MeasuringPointModel extends CompositeModel {
    public equipmentNumber: TextModel;
    public equipmentDescription: TextModel;
    public measuringPointID: TextModel;
    public measuringPointDesc: TextModel;
    public oldValue: string;
    public newValue: NumberModel;
    public isMandatory = '';
    public extUnit: TextModel;
    public isNotAvailable: DisplayDecorator<BooleanModel>;

    protected clientManager: ClientManagerInterface;
    public constructor(equipment?: EquipmentModel) {
        super('MeasuringPointModel');
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const translate = injector.get(TranslateService);
        this.clientManager = injector.get(ClientManagerInstanceToken);
        // equipment can be null if this constructor gets call from MeasuringPointsFromJson to restore saved values (see below)
        this.equipmentNumber = <TextModel>this.set(new TextModel('Equipment', equipment ? equipment.equnr.value : ''));
        this.equipmentDescription = <TextModel>(
            this.set(new TextModel('Description', equipment ? equipment.equiShortDesc.value : ''))
        );
        this.measuringPointID = <TextModel>(
            this.set(new TextModel('MeasuringPoint', equipment ? equipment.measuringPoint.value : ''))
        );
        this.measuringPointDesc = <TextModel>(
            this.set(new TextModel('MeasuringPointText', equipment ? equipment.measuringPointDesc.value : ''))
        );
        this.oldValue = equipment ? equipment.intMerkmal.value : '';

        // Measuring points are only mandatory in P58; for NGMAA the label can be ignored (SHSNGMAA-327)
        if (this.clientManager.isNgmP58() && equipment) {
            // mandatory field in equipment object is Number Model and same used in html as string value so created this model as string.
            this.isMandatory = equipment.mandatory.value;
        }

        this.extUnit = <TextModel>(
            this.set(new TextModel('Technical', equipment ? equipment.extUnit.value.toString() : ''))
        );
        this.isNotAvailable = new DisplayDecorator(
            <string>translate.instant('CONFIRMATION_MEASURING_POINTS_NOT_AVAILABLE_LABEL'),
            new BooleanModel('IsNotAvailable', false),
        );
        modelOptionsLoader.load(this.isNotAvailable, 'MeasuringPoints', 'IsNotAvailable');
        this.newValue = <NumberModel>this.set(new NumberModel('MeasuringValue', undefined));
        if (equipment) {
            this.newValue = assignModelOptions(
                this.newValue,
                { ...this.newValue.options, unit: equipment.extUnit.value },
                this,
            );
        }
        modelOptionsLoader.load(this.newValue, 'MeasuringPoints', 'NewValue');
    }

    public getOutModel(): ODataMeasuringPointOutModel {
        const outModel = <ODataMeasuringPointOutModel>super.getOutModel();
        const notAvailableValue = this.clientManager.isNgmAA() ? ConfigConstants.measuringPointValueNotAvailable : '';
        outModel.MeasuringValue = outModel.MeasuringValue ? outModel.MeasuringValue.toString() : notAvailableValue;

        return outModel;
    }
}

export class MeasuringPointModelFromJson extends MeasuringPointModel {
    public constructor(data: JsonMeasuringPointModel) {
        super();
        this.equipmentNumber.value = data.equipmentNumber.value;
        this.equipmentDescription.value = data.equipmentDescription.value;
        this.measuringPointID.value = data.measuringPointID.value;
        this.measuringPointDesc.value = data.measuringPointDesc.value;
        this.oldValue = data.oldValue;
        this.isMandatory = data.isMandatory;
        this.extUnit.value = data.extUnit.value;
        this.isNotAvailable.model.value = <boolean>data.isNotAvailable.model.value;
        this.newValue.value = data.newValue.value;
    }
}

export interface JsonMeasuringPointModel {
    equipmentNumber: JsonTextModel;
    equipmentDescription: JsonTextModel;
    measuringPointID: JsonTextModel;
    measuringPointDesc: JsonTextModel;
    oldValue: string;
    newValue: JsonNumberModel;
    isMandatory: string;
    extUnit: JsonTextModel;
    isNotAvailable: JsonDisplayDecorator;
}

export interface ODataMeasuringPointOutModel extends ODataObject {
    Equipment: string;
    Description: string;
    MeasuringPoint: string;
    MeasuringPointText: string;
    MeasuringValue: string;
    Technical: string;
}
